import { useState } from 'react';
import { tokens } from '@ekelhaft-tools/eitelkeit/tokens';
import type { BlueGreenSlotsProps, BlueGreenSlot } from './types.js';

function Slot({ slot, onClick }: { slot: BlueGreenSlot; onClick: (() => void) | null }) {
  const slotColor = slot.color === 'blue' ? tokens.colors.info : tokens.colors.mint;
  const isActive = slot.status === 'active';
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      onClick={onClick ?? undefined}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        backgroundColor: tokens.colors.surface,
        borderRadius: tokens.radii.md,
        padding: tokens.spacing.lg,
        border: `2px solid ${isActive ? slotColor : tokens.colors.border}`,
        cursor: onClick ? 'pointer' : 'default',
        transition: tokens.transitions.all,
        opacity: slot.status === 'idle' ? 0.6 : 1,
        ...(isHovered &&
          onClick && {
            borderColor: slotColor,
            boxShadow: `0 0 12px ${slotColor}`,
          }),
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: tokens.spacing.sm,
          marginBottom: tokens.spacing.md,
        }}
      >
        <div
          style={{
            width: '12px',
            height: '12px',
            borderRadius: '50%',
            backgroundColor: slotColor,
            boxShadow: isActive ? `0 0 12px ${slotColor}` : 'none',
          }}
        />
        <span
          style={{
            color: slotColor,
            fontSize: tokens.fontSizes.md,
            fontFamily: tokens.fonts.mono,
            fontWeight: 700,
            textTransform: 'uppercase',
          }}
        >
          {slot.color}
        </span>
      </div>

      {/* Version */}
      <div style={{ marginBottom: tokens.spacing.md }}>
        <div
          style={{
            color: tokens.colors.textMuted,
            fontSize: tokens.fontSizes.xs,
            fontFamily: tokens.fonts.mono,
            marginBottom: tokens.spacing.xs,
          }}
        >
          Version
        </div>
        <div
          style={{
            color: tokens.colors.textPrimary,
            fontSize: tokens.fontSizes.md,
            fontFamily: tokens.fonts.mono,
            fontWeight: 600,
          }}
        >
          {slot.version}
        </div>
      </div>

      {/* Traffic */}
      <div>
        <div
          style={{
            color: tokens.colors.textMuted,
            fontSize: tokens.fontSizes.xs,
            fontFamily: tokens.fonts.mono,
            marginBottom: tokens.spacing.xs,
          }}
        >
          Traffic
        </div>
        <div
          style={{
            color: isActive ? slotColor : tokens.colors.textMuted,
            fontSize: tokens.fontSizes.xl,
            fontFamily: tokens.fonts.mono,
            fontWeight: 700,
          }}
        >
          {slot.traffic}%
        </div>
      </div>

      {/* Status badge */}
      <div
        style={{
          marginTop: tokens.spacing.md,
          padding: `${tokens.spacing.xs} ${tokens.spacing.sm}`,
          backgroundColor: isActive ? `${slotColor}22` : tokens.colors.voidLight,
          borderRadius: tokens.radii.sm,
          textAlign: 'center',
        }}
      >
        <span
          style={{
            color: isActive ? slotColor : tokens.colors.textMuted,
            fontSize: tokens.fontSizes.xs,
            fontFamily: tokens.fonts.mono,
            fontWeight: 600,
            textTransform: 'uppercase',
          }}
        >
          {slot.status}
        </span>
      </div>
    </div>
  );
}

/**
 * BlueGreenSlots - Blue/Green deployment visualization
 *
 * Shows two deployment slots with version, traffic, and status
 */
export function BlueGreenSlots({ blue, green, onSlotClick }: BlueGreenSlotsProps) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: tokens.spacing.lg,
      }}
    >
      <Slot slot={blue} onClick={onSlotClick ? () => onSlotClick('blue') : null} />
      <Slot slot={green} onClick={onSlotClick ? () => onSlotClick('green') : null} />
    </div>
  );
}
